import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAdapter, useTopology } from "@/store/site";
import { pushToast } from "@/hooks/useToasts";

/**
 * Operator-set "value of one discharged kWh" — what the site would
 * otherwise pay to import that energy from the grid. Powers the Revenue
 * page's discharge-revenue chart. Persisted per-site on the site Asset's
 * SERVER_SCOPE attributes (live mode → TB; mock mode → localStorage,
 * via the adapter abstraction).
 *
 * Contract — same shape as the older localStorage-only `useDischargePrice`
 * helper so the BessSavingsBrowser refactor is a near-drop-in:
 *
 *   - `text`    raw input string (keeps partial entries like "0." editable)
 *   - `setText` controlled-input setter; persists to the adapter with a
 *               short debounce so we don't write on every keystroke.
 *               Empty/whitespace clears the attribute (chart returns to
 *               its empty state).
 *   - `price`   clamped numeric — `max(0, parseFloat(text) || 0)` —
 *               what every chart/computation should use.
 *   - `isReady` `false` on first load while the initial fetch resolves;
 *               consumers can avoid flashing the empty state before the
 *               stored value arrives.
 */

/** TB SERVER_SCOPE attribute key on the site Asset. */
export const DISCHARGE_PRICE_ATTR = "ems_discharge_price_eur_kwh";

/** Debounce so a typed "0.245" doesn't fire four TB writes. */
const SAVE_DEBOUNCE_MS = 350;

export function useDischargePrice(): {
  text: string;
  setText: (next: string) => void;
  price: number;
  isReady: boolean;
} {
  const adapter = useAdapter();
  const topology = useTopology();
  const queryClient = useQueryClient();

  // Query key includes the site id so a site swap refetches and resets the
  // input. Stale-fresh for 30 s — the attribute rarely changes server-side.
  const queryKey = ["site-attr", topology.id, DISCHARGE_PRICE_ATTR] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => adapter.getSiteAttribute(DISCHARGE_PRICE_ATTR),
    staleTime: 30_000,
  });

  const [text, setText] = useState<string>("");
  // Tracks the adapter's last-known value so a fetch only resets the input
  // when the upstream value actually changes — never while the user is in
  // the middle of typing in response to their OWN debounced write.
  const lastSeenRef = useRef<unknown>(Symbol("init"));
  useEffect(() => {
    const v = query.data;
    if (v === lastSeenRef.current) return;
    lastSeenRef.current = v;
    if (v === null || v === undefined) {
      setText("");
    } else if (typeof v === "number") {
      // Strip trailing zeros for a tidy display ("0.18" not "0.1800000").
      setText(String(v));
    } else if (typeof v === "string") {
      setText(v);
    }
  }, [query.data]);

  const debounceRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    },
    [],
  );

  const setTextAndPersist = (next: string) => {
    setText(next);
    if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      const trimmed = next.trim();
      let toSave: number | null;
      if (trimmed === "") {
        toSave = null;
      } else {
        const n = Number.parseFloat(trimmed);
        toSave = Number.isFinite(n) ? n : null;
      }
      // Optimistic update — the query cache reflects what we just sent so
      // the lastSeenRef effect doesn't re-write the input mid-typing.
      lastSeenRef.current = toSave;
      queryClient.setQueryData(queryKey, toSave);
      try {
        await adapter.setSiteAttribute(DISCHARGE_PRICE_ATTR, toSave);
      } catch {
        // Network/permission failure (MKT-07): without this the chart and
        // input keep showing a price the server never accepted, and the
        // next reload silently reverts it. Tell the operator and re-sync
        // the input to the server's value.
        pushToast({
          level: "critical",
          title: "Price not saved",
          detail:
            "The per-site price couldn't be written to the server. The field reverts to the last saved value — edit it again to retry.",
        });
        // Fresh sentinel so the refetched server value always re-syncs the
        // input, even if it compares equal to the optimistic one.
        lastSeenRef.current = Symbol("revert");
        await queryClient.invalidateQueries({ queryKey });
      }
    }, SAVE_DEBOUNCE_MS);
  };

  const price = Math.max(0, Number.parseFloat(text) || 0);
  return { text, setText: setTextAndPersist, price, isReady: !query.isLoading };
}
